import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/contexts/axiosApiInstance";
import type { QkindContext } from "./types";

// Server cost is ~2 short PG queries; clients still benefit from caching to
// avoid a re-fetch every time the user toggles a chip.
const TIMEOUT_MS = 8000;
const LRU_SIZE = 16;

export type QkindContextState =
  | { status: "idle"; result: QkindContext | null }
  | { status: "loading"; result: QkindContext | null }
  | { status: "error"; error: string; result: QkindContext | null };

interface LruEntry {
  key: string;
  value: QkindContext;
}

function lruGet(cache: LruEntry[], key: string): QkindContext | null {
  const idx = cache.findIndex((e) => e.key === key);
  if (idx === -1) return null;
  const [hit] = cache.splice(idx, 1);
  cache.unshift(hit);
  return hit.value;
}

function lruSet(cache: LruEntry[], key: string, value: QkindContext): void {
  const idx = cache.findIndex((e) => e.key === key);
  if (idx !== -1) cache.splice(idx, 1);
  cache.unshift({ key, value });
  if (cache.length > LRU_SIZE) cache.pop();
}

/**
 * Fetch the ancestor chain + immediate children of a pinned quantity kind.
 * Returns `{ status: "idle", result: null }` when `uri` is null — the
 * picker's idle state has nothing pinned and the strip falls back to the
 * search-result / heuristic logic.
 */
export function useQkindContext(
  uri: string | null | undefined,
): QkindContextState {
  const [state, setState] = useState<QkindContextState>({
    status: "idle",
    result: null,
  });
  const cacheRef = useRef<LruEntry[]>([]);

  useEffect(() => {
    if (!uri) {
      setState({ status: "idle", result: null });
      return;
    }
    const cached = lruGet(cacheRef.current, uri);
    if (cached) {
      setState({ status: "idle", result: cached });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState((prev) => ({ status: "loading", result: prev.result }));

    axiosInstance
      .get<QkindContext>("/api/units/qkind-context", {
        params: { uri },
        signal: controller.signal,
        timeout: TIMEOUT_MS,
      })
      .then((res) => {
        if (cancelled) return;
        lruSet(cacheRef.current, uri, res.data);
        setState({ status: "idle", result: res.data });
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        const isTimeout =
          err?.code === "ECONNABORTED" || err?.message?.includes("timeout");
        const msg = isTimeout
          ? "Hierarchy lookup timed out"
          : "Hierarchy lookup failed";
        setState((prev) => ({
          status: "error",
          error: msg,
          result: prev.result,
        }));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [uri]);

  return state;
}
